// src/pages/negocio/MensajesNegocio.js
//
// Mensajería del negocio con los vecis. Espejo de MensajesVeci pero
// desde el lado del tendero. Polling 5s.
//
// Rutas:
// - /negocio/mensajes                → lista de conversaciones
// - /negocio/mensajes/:veciId        → chat con un veci

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Stack, Card, CardActionArea, CardContent, Avatar, Chip,
  Alert, CircularProgress, TextField, IconButton, Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon  from '@mui/icons-material/ArrowBack';
import SendIcon       from '@mui/icons-material/Send';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import PersonIcon     from '@mui/icons-material/Person';

import { chat } from '../../api/chatApi';

const POLLING_MS = 5000;

export default function MensajesNegocio() {
  const { veciId } = useParams();
  if (veciId) return <VistaChat veciId={parseInt(veciId, 10)} />;
  return <ListaConversaciones />;
}

function ListaConversaciones() {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await chat.conversaciones();
      const list = Array.isArray(r) ? r : (r.items || r.Items || []);
      setItems(list);
    } catch (e) { setError(e.mensajeUsuario || 'No se pudieron cargar las conversaciones.'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, POLLING_MS);
    return () => clearInterval(id);
  }, [cargar]);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="overline" color="text.secondary">Mensajes</Typography>
        <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Conversaciones con vecis</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <ChatBubbleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">Aún no hay conversaciones.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {items.map((c) => {
            const veciId  = c.veciId || c.VeciId || c.clienteId;
            const nombre  = c.veciNombre || c.VeciNombre || c.contraparteNombre || c.clienteNombre;
            const noLeidos = c.noLeidos || c.NoLeidos || 0;
            const ultimoTexto = c.ultimoMensaje || c.UltimoMensaje;
            const ultimaFecha = c.ultimaActividad || c.UltimaActividad;
            return (
              <Card key={c.conversacionId || c.ConversacionId}>
                <CardActionArea onClick={() => navigate(`/negocio/mensajes/${veciId}`)}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'warning.main' }}><PersonIcon /></Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>{nombre}</Typography>
                      {ultimoTexto && (
                        <Typography variant="body2" color="text.secondary" noWrap>{ultimoTexto}</Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      {ultimaFecha && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(ultimaFecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                      {noLeidos > 0 && <Chip label={noLeidos} size="small" color="primary" />}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

function VistaChat({ veciId }) {
  const navigate = useNavigate();
  const [conversacionId, setConvId] = useState(null);
  const [nombre, setNombre]         = useState('Veci');
  const [mensajes, setMensajes]     = useState([]);
  const [texto, setTexto]           = useState('');
  const [cargando, setCargando]     = useState(true);
  const [enviando, setEnviando]     = useState(false);
  const [error, setError]           = useState('');
  const contenedorRef = useRef(null);

  const cargarInicial = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const convs = await chat.conversaciones();
      const list = Array.isArray(convs) ? convs : (convs.items || []);
      const existente = list.find(c => (c.veciId || c.VeciId || c.clienteId) === veciId);
      if (existente) {
        const id = existente.conversacionId || existente.ConversacionId;
        setConvId(id);
        setNombre(existente.veciNombre || existente.VeciNombre || existente.clienteNombre || 'Veci');
        const det = await chat.detalle(id);
        setMensajes(det.mensajes || det.Mensajes || []);
        await chat.marcarVisto(id).catch(() => {});
      }
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar el chat.'); }
    finally { setCargando(false); }
  }, [veciId]);

  useEffect(() => { cargarInicial(); }, [cargarInicial]);

  useEffect(() => {
    if (!conversacionId) return;
    const id = setInterval(async () => {
      try {
        const det = await chat.detalle(conversacionId);
        setMensajes(det.mensajes || det.Mensajes || []);
      } catch { /* ignore */ }
    }, POLLING_MS);
    return () => clearInterval(id);
  }, [conversacionId]);

  useEffect(() => {
    if (contenedorRef.current) contenedorRef.current.scrollTop = contenedorRef.current.scrollHeight;
  }, [mensajes]);

  const enviar = async () => {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      await chat.enviar({ veciId, texto: texto.trim() });
      setTexto('');
      if (conversacionId) {
        const det = await chat.detalle(conversacionId);
        setMensajes(det.mensajes || det.Mensajes || []);
      } else {
        cargarInicial();
      }
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo enviar el mensaje.'); }
    finally { setEnviando(false); }
  };

  return (
    <Stack spacing={2} sx={{ height: 'calc(100vh - 120px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => navigate('/negocio/mensajes')}><ArrowBackIcon /></IconButton>
        <Avatar sx={{ bgcolor: 'warning.main' }}><PersonIcon /></Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>{nombre}</Typography>
          <Typography variant="caption" color="text.secondary">Chat en vivo</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 300 }}>
        <Box ref={contenedorRef} sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#FBF7F0' }}>
          {cargando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : mensajes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Aún no hay mensajes. Envía el primero.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {mensajes.map((m, i) => {
                const esMio = (m.emisorTipo || m.EmisorTipo) === 'NEGOCIO';
                const texto = m.texto || m.Texto || m.contenido;
                const fecha = m.fecha || m.Fecha;
                return (
                  <Box key={i} sx={{ display: 'flex', justifyContent: esMio ? 'flex-end' : 'flex-start' }}>
                    <Box sx={{
                      bgcolor: esMio ? 'primary.main' : 'background.paper',
                      color:   esMio ? 'primary.contrastText' : 'text.primary',
                      p: 1.5, borderRadius: 2, maxWidth: '75%', boxShadow: 1,
                    }}>
                      <Typography variant="body2">{texto}</Typography>
                      {fecha && (
                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
                          {new Date(fecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth placeholder="Escribe un mensaje…"
            value={texto} onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
            multiline maxRows={4} size="small"
          />
          <IconButton onClick={enviar} disabled={enviando || !texto.trim()} color="primary">
            <SendIcon />
          </IconButton>
        </Box>
      </Card>
    </Stack>
  );
}
